import { DataTable } from "@/components/datatable/components/DataTable";
import { tableSearchSchema, safeDecompress } from "@/components/datatable/hooks/useTableUrlState";
import { mapTableStateToQueryParams } from "@/components/datatable/utils";
import PageHeader from "@/components/common/PageHeader";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import useConfirm from "@/hooks/useConfirm";
import { transactionsKeys, statsKeys } from "@/query-keys";
import { type TransactionListItem } from "@/schemas/transactionSchema";
import { services } from "@/services";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { FileSpreadsheet, Loader2 } from "lucide-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import CreateTransactionForm from "./_components/CreateTransactionForm";
import TransactionsKpis from "./_components/TransactionsKpis";
import useColumns from "./_hooks/useColumns";
import useTransactionMutation from "./_hooks/useTransactionMutation";
import useTransactionsQuery from "./_hooks/useTransactionsQuery";
import useTransactionExport from "./_hooks/useTransactionExport";

export const Route = createFileRoute("/_private/transactions/")({
  validateSearch: tableSearchSchema,
  loaderDeps: ({ search }) => ({
    q: search.q,
  }),
  loader: async ({ deps, context: { queryClient } }) => {
    const tableState = safeDecompress(deps.q);
    const requestParams = mapTableStateToQueryParams(tableState);
    const [transactions] = await Promise.all([
      queryClient.ensureQueryData({
        queryKey: transactionsKeys.lists(deps.q),
        queryFn: () => services.getTransactions(requestParams),
      }),
      queryClient.ensureQueryData({
        queryKey: statsKeys.transactions(),
        queryFn: () => services.getTransactionStats(),
      }),
    ]);
    return transactions;
  },
  component: RouteComponent,
  errorComponent: ({ error }) => <ErrorBoundary error={error} />,
});

function RouteComponent() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const loaderData = Route.useLoaderData();
  const { data: queryData } = useTransactionsQuery(search.q, loaderData);

  const columns = useColumns();
  const { deleteTransaction, isDeleting } = useTransactionMutation();
  const { exportTransactions, isExporting } = useTransactionExport();

  const deleteConfirm = useConfirm({
    title: t("transactions.confirm.delete.title"),
    description: t("transactions.confirm.delete.description"),
    isPending: isDeleting,
  });

  const handleView = useCallback(
    (row: TransactionListItem) =>
      navigate({ to: "/transactions/$transactionId", params: { transactionId: row.id } }),
    [navigate],
  );

  const handleDelete = useCallback(
    (row: TransactionListItem) => {
      deleteConfirm.confirm(() => {
        deleteTransaction(row.id);
      });
    },
    [deleteConfirm, deleteTransaction],
  );

  return (
    <div className="flex-1 flex flex-col gap-6">
      <PageHeader
        title={t("transactions.list.title")}
        subtitle={t("transactions.list.subtitle")}
        action={<CreateTransactionForm />}
      />

      <TransactionsKpis />

      <DataTable<TransactionListItem>
        searchPlaceholder={t("transactions.list.searchPlaceholder")}
        data={queryData?.items}
        nextCursor={queryData?.nextCursor}
        hasMore={queryData?.hasMore}
        columns={columns}
        queryKey={transactionsKeys.lists(search.q)}
        navigateFrom="/transactions"
        searchFrom="/_private/transactions/"
        onView={handleView}
        onDelete={handleDelete}
        headerActions={
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                onClick={() => exportTransactions(search.q)}
                disabled={isExporting}
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileSpreadsheet className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("charges.list.actions.exportXlsx")}</TooltipContent>
          </Tooltip>
        }
        actions={[]}
        searchable={true}
      />

      {deleteConfirm.confirmDialog}
    </div>
  );
}
